import { clickIdToReferer } from './constants';

const ADS_CLICK_ID_COOKIE = 'ttt_ads_click_id';
const ADS_CLICK_ID_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

export type AdsClickId = Partial<
  Record<keyof typeof clickIdToReferer, string>
> & {
  /** ISO date of the first ads visit, kept when a new ads visit replaces the click id */
  adsFirstClickAt?: string;
  /** Ads account of the visit (accountid url parameter) */
  adsAccountId?: string;
};

/**
 * @description Reads the ads click id kept for 7 days after the last ads visit.
 * It is a functional cookie (it sets the ads fees), so it is set without consent.
 * @returns {AdsClickId} The stored click id, empty if there is none
 */
export const readAdsClickId = (): AdsClickId => {
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${ADS_CLICK_ID_COOKIE}=`));

  try {
    return cookie
      ? JSON.parse(
          decodeURIComponent(cookie.slice(ADS_CLICK_ID_COOKIE.length + 1)),
        )
      : {};
  } catch {
    return {};
  }
};

/**
 * @description Stores the ads click id for 7 days. A new ads visit replaces it,
 * but keeps the date of the first ads visit.
 * @param {AdsClickId} clickId The click id read from the url
 */
export const saveAdsClickId = (clickId: AdsClickId): void => {
  const value: AdsClickId = {
    ...clickId,
    adsFirstClickAt:
      readAdsClickId().adsFirstClickAt || new Date().toISOString(),
  };

  document.cookie = `${ADS_CLICK_ID_COOKIE}=${encodeURIComponent(
    JSON.stringify(value),
  )}; max-age=${ADS_CLICK_ID_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
};

/**
 * @description Returns the referer of the stored click id, ie GADS or BING.
 * @param {AdsClickId} clickId The stored click id
 * @returns {string | undefined} The referer
 */
export const getAdsClickIdReferer = (clickId: AdsClickId): string | undefined =>
  clickIdToReferer[Object.keys(clickId).find((key) => clickIdToReferer[key])];
